import React from 'react';
import DualRangeSlider from './components/DualRangeSlider';
import { withRouter, useParams } from "react-router";
import {
  Container, 
  Row, 
  Col, ColBr,
  Img,
  Link,
} from './StyledComponents.js'
import styled from 'styled-components'
import Spinner from './components/Spinner'
import AmpSafeImg from './AmpSafeImg'
import { useQuery, gql } from '@apollo/client'
import { useHistory } from "react-router-dom"
import moment from 'moment'
import { useIntl, FormattedMessage } from "react-intl"

import {Helmet} from "react-helmet-async"
import { useLocation } from 'react-router-dom'
import altTextKes from './utils/altTextKes'
import useTenant from './client/useTenant'
import useSsr from './useSsr'
import {useCsEvent} from './client/useEvents'
import CommonPagination from './CommonPagination'

const queryStringParser = require('query-string')

const KupurImg = styled(AmpSafeImg)`
  max-height: 300px;
  margin-top: auto;
`
const KupurLink = styled(Link)`
  display: flex;
  flex-direction: column;
  height:100%;
  max-height:500px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 16px 12px 12px 12px;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  &:hover {
    box-shadow: 0 6px 24px rgba(0,0,0,0.16);
    transform: translateY(-4px) scale(1.02);
  }
  & img {
    object-fit:contain;
    border-radius: 8px;
    transition: box-shadow 0.2s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }
  &:hover img {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }
`
const SayfaImg = styled(AmpSafeImg)`
  max-height: 450px;
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  border-radius: 8px;
`


const gqlQuery = gql`
  query AraQuery($query: String!, $offset: Int!, $count: Int!,
      $startDate: String, $endDate: String, 
      $gazete: String, $esnek: Boolean) {
    arama(query: $query, offset: $offset, count: $count, 
        startDate: $startDate, endDate: $endDate, gazete: $gazete, esnek: $esnek) {
      sayfalar {
        gazete, tarih,
        sayfa, thumbnail,
        caption, alt_text
      },
      kupurler {
        id, dosya, tarih, icerik, 
        gazete {
          gazete
        }
      },
      toplam
    },
    gazeteler {gazete, gorunen_ad}
  }
`
const PAGE_SIZE = 50;


function AraPageComponent() {
  const location = useLocation();
  const intl = useIntl();
  const tenant = useTenant();
  const { context: ssrContext } = useSsr();
  const fireCsEvent = useCsEvent();
  const history = useHistory();

  const queryParams = queryStringParser.parse(location.search);
  let { query, page } = useParams();
  query = query || queryParams.q;
  page = parseInt(page || 1);

  // State for filters
  const [selectedGazete, setSelectedGazete] = React.useState(queryParams.gazete || "");
  // Range slider for year
  const [yearRange, setYearRange] = React.useState([
    queryParams.startDate ? parseInt(queryParams.startDate.slice(0, 4)) : 1928,
    queryParams.endDate ? parseInt(queryParams.endDate.slice(0, 4)) : new Date().getFullYear()
  ]);
  const [minYear, setMinYear] = React.useState(1928); // fallback min year
  const [maxYear, setMaxYear] = React.useState(new Date().getFullYear());
  // Collapsible state
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  // Sync filter state with URL query params (for browser navigation/external changes)
  React.useEffect(() => {
    // Parse again in case location.search changed
    const params = queryStringParser.parse(location.search);
    // Update gazete selection if changed
    setSelectedGazete(params.gazete || "");
    // Update year range if changed
    const startYear = params.startDate ? parseInt(params.startDate.slice(0, 4)) : minYear;
    const endYear = params.endDate ? parseInt(params.endDate.slice(0, 4)) : maxYear;
    setYearRange([
      isNaN(startYear) ? minYear : startYear,
      isNaN(endYear) ? maxYear : endYear
    ]);
  }, [location.search, minYear, maxYear]);

  // Fetch gazeteler and available years
  const { data, loading } = useQuery(gqlQuery, {
    variables: {
      query: query,
      count: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      startDate: yearRange[0] ? `${yearRange[0]}-01-01` : queryParams.startDate,
      endDate: yearRange[1] ? `${yearRange[1]}-12-31` : queryParams.endDate,
      esnek: queryParams.esnek === "t",
      gazete: selectedGazete || queryParams.gazete,
    },
  });

  // Extract years from gazeteler if available
  React.useEffect(() => {
    if (data?.gazeteler && data.gazeteler.length > 0) {
      // If you have a years field in API, use it. Otherwise, fallback to static.
      // setMinYear(...)
      // setMaxYear(...)
    }
  }, [data]);

  const { kupurler, sayfalar, toplam } = data?.arama || {};
  const gazeteler = data?.gazeteler?.reduce((accu, gazete) => {
    accu[gazete.gazete] = gazete;
    return accu;
  }, {});

  if (!loading && !sayfalar?.length) {
    ssrContext.notFound = true;
  }

  // Handle filter changes
  const handleGazeteChange = (e) => {
    setSelectedGazete(e.target.value);
    history.replace({
      search: queryStringParser.stringify({
        ...queryParams,
        gazete: e.target.value || undefined,
        startDate: `${yearRange[0]}-01-01`,
        endDate: `${yearRange[1]}-12-31`,
        page: 1,
      }),
    });
  };

  // For double-ended slider, handle both values
  const handleYearRangeChange = (e, idx) => {
    let newRange = [...yearRange];
    newRange[idx] = parseInt(e.target.value);
    // Ensure start <= end
    if (newRange[0] > newRange[1]) {
      if (idx === 0) newRange[1] = newRange[0];
      else newRange[0] = newRange[1];
    }
    setYearRange(newRange);
    history.replace({
      search: queryStringParser.stringify({
        ...queryParams,
        startDate: `${newRange[0]}-01-01`,
        endDate: `${newRange[1]}-12-31`,
        gazete: selectedGazete || undefined,
        page: 1,
      }),
    });
  };

  // Remove year range filter
  const clearYearRange = () => {
    setYearRange([minYear, maxYear]);
    history.replace({
      search: queryStringParser.stringify({
        ...queryParams,
        startDate: undefined,
        endDate: undefined,
        gazete: selectedGazete || undefined,
        page: 1,
      }),
    });
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>{intl.formatMessage({ id: "ara.meta.title", defaultMessage: "{query} | Gaste Arşivi" }, { query })}</title>
        <meta property="og:title" content={intl.formatMessage({ id: "ara.meta.title", defaultMessage: "{query} | Gaste Arşivi" }, { query })} />
        <meta property="og:description" content={intl.formatMessage({ id: "ara.meta.desc" }, { query })} />
        <meta name="description" content={intl.formatMessage({ id: "ara.meta.desc" }, { query })} />
      </Helmet>

      {/* Collapsible Modernized Filter UI */}
      <Container>
        <Row className="justify-content-center mb-3 mt-3">
          <Col xs={12} md={10} lg={8}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <button
                type="button"
                onClick={() => {
                  setFiltersOpen(o => !o);
                  fireCsEvent && fireCsEvent({
                    category: "Ara Page",
                    action: "Engagement",
                    label: "Filters button click"
                  });
                }}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 16,
                  padding: '8px 18px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                aria-expanded={filtersOpen}
                aria-controls="filters-collapse-area"
              >
                <span style={{ fontSize: 18, display: 'inline-block', transform: filtersOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▶</span>
                <FormattedMessage id="ara.filters" defaultMessage="Filtreler" />
              </button>
              {/* Show active filters summary */}
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedGazete && (
                  <span style={{ background: '#e3e8ef', borderRadius: 8, padding: '4px 10px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {data?.gazeteler?.find(g => g.gazete === selectedGazete)?.gorunen_ad || selectedGazete}
                    <button
                      type="button"
                      aria-label="Filtreyi kaldır"
                      onClick={() => handleGazeteChange({ target: { value: "" } })}
                      style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', marginLeft: 2 }}
                    >×</button>
                  </span>
                )}
                {(yearRange[0] !== minYear || yearRange[1] !== maxYear) && (
                  <span style={{ background: '#e3e8ef', borderRadius: 8, padding: '4px 10px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {yearRange[0]} - {yearRange[1]}
                    <button
                      type="button"
                      aria-label="Yıl filtresini kaldır"
                      onClick={clearYearRange}
                      style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', marginLeft: 2 }}
                    >×</button>
                  </span>
                )}
              </div>
            </div>
            <div id="filters-collapse-area" style={{
              maxHeight: filtersOpen ? 500 : 0,
              overflow: 'hidden',
              transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
              marginBottom: filtersOpen ? 16 : 0
            }}>
              {filtersOpen && (
                <div style={{
                  background: '#f8fafc',
                  borderRadius: 16,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  padding: '24px 20px 16px 20px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 24,
                  justifyContent: 'space-between',
                  minHeight: 80
                }}>
                  {/* Newspaper Filter */}
                  <div style={{ flex: 1, minWidth: 220, maxWidth: 320 }}>
                    <label htmlFor="gazete-select" style={{ fontWeight: 500, marginBottom: 6, display: 'block' }}>
                      <FormattedMessage id="ara.gazete" defaultMessage="Gazete:" />
                    </label>
                    <div style={{ position: 'relative' }}>
                      <select
                        id="gazete-select"
                        className="form-control"
                        style={{ paddingRight: selectedGazete ? 32 : undefined, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 16 }}
                        value={selectedGazete}
                        onChange={handleGazeteChange}
                      >
                        <option value="">Tümü</option>
                        {data?.gazeteler?.map(g => (
                          <option key={g.gazete} value={g.gazete}>{g.gorunen_ad}</option>
                        ))}
                      </select>
                      {selectedGazete && (
                        <button
                          type="button"
                          aria-label="Filtreyi kaldır"
                          onClick={() => handleGazeteChange({ target: { value: "" } })}
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 18,
                            color: '#888',
                            padding: 0,
                            zIndex: 2
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Year Filter (Combined double-ended slider) */}
                  <div style={{ flex: 1, minWidth: 220, maxWidth: 340 }}>
                    <label style={{ fontWeight: 500, marginBottom: 6, display: 'block' }}>
                      <FormattedMessage id="ara.yil" defaultMessage="Yıl aralığı:" />
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', height: 56 }}>
                      <DualRangeSlider
                        min={minYear}
                        max={maxYear}
                        value={yearRange}
                        onChange={newRange => {
                          setYearRange(newRange);
                          history.replace({
                            search: queryStringParser.stringify({
                              ...queryParams,
                              startDate: `${newRange[0]}-01-01`,
                              endDate: `${newRange[1]}-12-31`,
                              gazete: selectedGazete || undefined,
                              page: 1,
                            }),
                          });
                        }}
                      />
                      <span style={{ fontWeight: 600, fontSize: 15, minWidth: 36, textAlign: 'center' }}>{yearRange[0]}</span>
                      <span style={{ fontWeight: 600, fontSize: 15, color: '#888' }}>-</span>
                      <span style={{ fontWeight: 600, fontSize: 15, minWidth: 36, textAlign: 'center' }}>{yearRange[1]}</span>
                      {(yearRange[0] !== minYear || yearRange[1] !== maxYear) && (
                        <button
                          type="button"
                          aria-label="Yıl filtresini kaldır"
                          onClick={clearYearRange}
                          style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            color: '#888',
                            fontSize: 16,
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >×</button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {(kupurler || sayfalar) ? (
        <Container>
          {kupurler?.length > 0 && (
            <React.Fragment>
              <Row className="m-1 mt-3">
                <Col>
                  <h2><FormattedMessage id="ara.kupurler" defaultMessage="Bulunan küpürler" /></h2>
                </Col>
              </Row>
              <Row style={{ rowGap: '32px' }}>
                {kupurler.map((kupur, i) => {
                  const gazete = gazeteler[kupur.gazete.gazete];
                  if (!gazete) {
                    console.error("Ara page'de bulunan küpürün gazetesi yok", kupur.gazete.gazete);
                  }
                  return (
                    <React.Fragment key={kupur.id}>
                      <ColBr className="mb-4" xs={12} sm={6} md={4} lg={3}>
                        <KupurLink className="text-center" to={`/kupur/${kupur.id}`}>
                          <h3 className='text-center' style={{ fontSize: '1.1rem', fontWeight: 600 }}>{gazete?.gorunen_ad} {moment(kupur.tarih).format('LL')}</h3>
                          <KupurImg
                            ampProps={{ width: 300, height: 450 }}
                            src={`${process.env.REACT_APP_GA_CDN_URL}/kupur-thumbnail/${kupur.dosya}`}
                            alt={altTextKes(kupur.icerik, 125)}
                            loading="lazy" width="300" height="450"
                          >
                            <Img />
                          </KupurImg>
                        </KupurLink>
                      </ColBr>
                    </React.Fragment>
                  );
                })}
              </Row>
            </React.Fragment>
          )}
          {sayfalar && sayfalar.length > 0 && (
            <React.Fragment>
              <Row><Col>
                <h2 className='mt-1 mb-1'>
                  <FormattedMessage
                    id="ara.sayfalar.baslik"
                    defaultMessage="{query} içeren sayfalar"
                    values={{ query }}
                  />
                </h2>
              </Col></Row>
              <Row style={{ rowGap: '32px' }}>
                {sayfalar.map((sayi, i) => {
                  return (
                    <React.Fragment key={sayi.id}>
                      <ColBr
                        itemScope itemType="https://schema.org/Article"
                        className="mb-1"
                      >
                        <KupurLink
                          itemProp="mainEntityOfPage"
                          className="text-center"
                          onClick={() => {
                            fireCsEvent({
                              category: "Ara",
                              action: "Navigation",
                              label: "Sonuc link click"
                            });
                          }}
                          to={`/gazete/${sayi.gazete}/${moment(sayi.tarih).format('YYYY-MM-DD')}/${sayi.sayfa}`}
                        >
                          <h3 className='text-center mt-2 mb-1' style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <FormattedMessage id="ara.sayfaTitle" defaultMessage="{ad} {tarih} sayfa {sayfa}"
                              values={{ ad: gazeteler[sayi.gazete]?.gorunen_ad, tarih: moment(sayi.tarih).format('LL'), sayfa: sayi.sayfa }} />
                          </h3>
                          <SayfaImg
                            src={`${process.env.REACT_APP_GA_CDN_URL}/${sayi.thumbnail}`}
                            ampProps={{ width: "300", height: "450" }}
                            alt={sayi.caption || altTextKes(sayi.alt_text, 125) || `Sayfa ${sayi.sayfa}`}
                            loading="lazy" width="300" height="450"
                          >
                            <Img />
                          </SayfaImg>
                        </KupurLink>
                      </ColBr>
                    </React.Fragment>
                  );
                })}
              </Row>
            </React.Fragment>
          )}
          <Row><Col>
            <CommonPagination
              page={page}
              total={toplam}
              pageSize={PAGE_SIZE}
              query={query}
              location={location}
              fireCsEvent={fireCsEvent}
              ariaLabel={intl.formatMessage({ id: "ara.pagination.arialabel", defaultMessage: "Sayfalar" })}
              urlGenerator={(targetPage, query) => targetPage === 1 ? `/ara/${query}` : `/ara/${query}/${targetPage}`}
              eventCategory="Gazete"
            />
          </Col></Row>
        </Container>
      ) : (
        <Row><Col className="mb-5 d-flex justify-content-center">
          <Spinner />
        </Col></Row>
      )}
    </React.Fragment>
  );
}

export default withRouter(AraPageComponent);
